import React, { useCallback, useMemo } from "react";

import { useI18n } from "../../../../hooks/i18n-hooks";

import VStack from "../../../../ui_atoms/VStack";
import ScreenCard, { ButtonType } from "../../../../screens/ScreenCard";

import { StackScreenProps } from "@react-navigation/stack";
import { IJMEditorStackParamList } from "../IJMEditorNavigator";
import { useRecoilState } from "recoil";
import { editIjmSelection } from "../../../../atoms/core";
import { useQuery } from "@apollo/client";
import { GET_IJM_CHOICES } from "../../../../graphql/client";
import LoadingBoundary from "../../../../ui_atoms/LoadingBoundary";
import ListItem, { buildDescription } from "../../../../ui_atoms/ListItem";
import { BodyMediumText, BodySmallText, TitleMediumText } from "../../../FormattedText";
import Gap from "../../../../ui_atoms/Gap";
import { useBuildGroupInfo } from "../helper";
import { useApolloErrorSnackbar } from "../../../../hooks/snackbar";
import Icon from "../../../Icon";
import { useTheme } from "../../../../hooks/use-theme";
import Separator from "../../../../ui_atoms/separator";

type IJMRootScreenProps = StackScreenProps<IJMEditorStackParamList, "GroupSelect">;

const IJMRootScreen: React.FC<IJMRootScreenProps> = ({ navigation }) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const [ijmSelection, setIjmSelection] = useRecoilState(editIjmSelection);
    const { data, loading, error } = useQuery(GET_IJM_CHOICES, {
        fetchPolicy: "cache-and-network",
    });
    useApolloErrorSnackbar(error);
    const ijmChoices = useMemo(() => {
        return data?.getIjmChoices || [];
    }, [data]);
    const onCancel = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    const buttons = useMemo<ButtonType[]>(() => {
        return [
            {
                title: i18n.t("close"),
                onPress: onCancel,
                mode: "outlined",
                isValid: true,
            },
        ];
    }, [i18n, onCancel]);

    const buildGroupInfo = useBuildGroupInfo();

    return (
        <ScreenCard buttons={buttons}>
            <VStack>
                <Gap gap={0.5} />
                <VStack h="2/3">
                    <TitleMediumText>{i18n.t("ijm.editor.group_select.title")}</TitleMediumText>
                    <Gap gap={1} />
                    <BodyMediumText>{i18n.t("ijm.editor.group_select.text_1")}</BodyMediumText>
                    <Gap gap={1} />
                    <TitleMediumText>{i18n.t("ijm.editor.group_select.title_2")}</TitleMediumText>
                </VStack>
                <Separator top={1 / 2} />
                <LoadingBoundary loading={loading}>
                    {ijmChoices.map((ijmChoice) => (
                        <ListItem
                            key={ijmChoice.id}
                            title={ijmChoice.name || undefined}
                            selectable
                            checked={ijmChoice.id === ijmSelection.ijmGroupId}
                            description={buildDescription(buildGroupInfo(ijmChoice), {
                                isList: true,
                            })}
                            onPress={() => {
                                setIjmSelection((prev) => ({
                                    ...prev,
                                    ijmGroupId: ijmChoice.id,
                                    ijmGroupName: ijmChoice.name,
                                }));
                                navigation.goBack();
                            }}
                        />
                    ))}
                </LoadingBoundary>
                <Gap gap={1} />
                <VStack h="2/3">
                    <BodySmallText color={colors.greyAccessible}>
                        <Icon size={14} color={colors.greyAccessible} name="error" />{" "}
                        {i18n.t("ijm.editor.group_select.renew_text")}
                    </BodySmallText>
                </VStack>
            </VStack>
        </ScreenCard>
    );
};

export default IJMRootScreen;
